import React, { useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Camera, Trash2 } from "lucide-react";
import { toast } from "sonner";
import StudentAvatar from "@/components/StudentAvatar";
import { YEAR_LABEL, TRACK_LABEL } from "@/lib/school";

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className="text-sm mt-0.5">{value || "—"}</div>
    </div>
  );
}

export default function PortalProfile() {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const { data: me, isLoading } = useQuery({ queryKey: ["my-student-profile"], queryFn: async () => (await api.get("/students/-me")).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["my-student-profile"] });

  async function upload(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post(`/students/${me.id}/photo`, fd); toast.success("Foto aggiornata"); refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || "Errore caricamento"); }
  }
  async function remove() {
    if (!window.confirm("Rimuovere la foto profilo?")) return;
    await api.delete(`/students/${me.id}/photo`);
    toast.success("Foto rimossa");
    refresh();
  }

  if (isLoading || !me) return <div className="p-10 text-center text-sm text-stone-400">Caricamento…</div>;
  const name = `${me.first_name || ""} ${me.last_name || ""}`.trim();
  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[900px] mx-auto">
      <PageHeader title="Il mio profilo" subtitle="La tua foto è visibile alla segreteria e ai docenti."/>
      <Card className="p-6 border border-stone-200 rounded-2xl shadow-none flex flex-col sm:flex-row gap-6 items-start">
        <div className="flex flex-col items-center gap-3">
          <StudentAvatar studentId={me.id} photoPath={me.photo_path} name={name} className="w-32 h-32 rounded-2xl text-3xl" testId="profile-avatar"/>
          <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" onChange={upload} data-testid="profile-photo-input"/>
          <Button size="sm" className="rounded-full bg-[#1C352D] hover:bg-[#264A3E]" onClick={() => fileRef.current?.click()} data-testid="profile-photo-upload">
            <Camera size={14} className="mr-1.5"/> {me.photo_path ? "Cambia foto" : "Carica foto"}
          </Button>
          {me.photo_path && (
            <button type="button" onClick={remove} className="text-[11px] text-stone-500 hover:text-red-600 flex items-center gap-1" data-testid="profile-photo-remove"><Trash2 size={11}/> Rimuovi</button>
          )}
          <div className="text-[10px] text-stone-400 text-center">PNG, JPG o WEBP · max 5 MB</div>
        </div>
        <div className="grid grid-cols-2 gap-4 flex-1">
          <div className="col-span-2"><div className="text-2xl font-semibold text-[#1C352D]" data-testid="profile-name">{name}</div></div>
          <Field label="Matricola" value={me.matricola}/>
          <Field label="Email" value={me.email}/>
          <Field label="Anno" value={YEAR_LABEL[me.year_level]}/>
          <Field label="Percorso" value={TRACK_LABEL[me.track_type]}/>
          <Field label="Anno accademico" value={me.academic_year}/>
          <Field label="Telefono" value={me.phone}/>
          <Field label="Assicurazione" value={me.has_insurance ? "Presente" : "Assente"}/>
        </div>
      </Card>
    </div>
  );
}
